import { useMemo, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Header,
  Container,
  ContentLayout,
  Box,
  Link,
  Button,
  SpaceBetween,
  Flashbar,
  ExpandableSection,
  Alert,
} from '@amzn/awsui-components-react';
import { useGetToolMetadataQuery, useReloadToolsBackendMutation } from 'src/store/apis/toolApi';
import { ToolMetadata } from 'src/store/interfaces/Tool.types';
import useNotification from '../../hooks/useNotification';
import { usePermissions } from '../../hooks/usePermissions';

const ToolsList: FC = () => {
  const navigate = useNavigate();
  const { addNotificationItem, notificationItems } = useNotification();
  const { canEditTools } = usePermissions();

  const { data: toolMetadata, isLoading, error } = useGetToolMetadataQuery();
  const [reloadToolsBackend, { isLoading: isReloading }] = useReloadToolsBackendMutation();

  const { toolsByCategory, totalTools } = useMemo(() => {
    if (!toolMetadata) return { toolsByCategory: {}, totalTools: 0 };
    
    // Group tools by category
    const grouped = toolMetadata.reduce((acc: Record<string, ToolMetadata[]>, tool: ToolMetadata) => {
      const category = tool.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(tool);
      return acc;
    }, {});

    return {
      toolsByCategory: grouped,
      totalTools: toolMetadata.length
    };
  }, [toolMetadata]);

  const handleRowClick = (item: ToolMetadata) => {
    navigate(`/tools/${item.toolId}`, {
      state: { 
        toolId: item.toolId,
      }
    });
  };

  const handleReloadToolsBackend = async () => {
    try {
      await reloadToolsBackend().unwrap();
      
      addNotificationItem({
        type: 'success',
        content: 'Successfully reloaded tools backend',
        id: 'tools-backend-reloaded',
      });

    } catch (error) {
      console.error('Reload error:', error);
      const errorMessage = (error as Error).message;

      addNotificationItem({
        type: 'error',
        content: `Error reloading tools backend: ${errorMessage}`,
        id: 'tools-backend-reload-error'
      });
    }
  };


  const columnDefinitions = useMemo(() => [
    {
      id: 'name',
      header: 'Tool Identifier',
      cell: (item: ToolMetadata) => (
        <Box fontSize="body-m">
          <Link onFollow={() => handleRowClick(item)}>
            <span 
              className="tool-identifier"
              title={item.toolId}
            >
              {item.toolId}
            </span>
          </Link>
        </Box>
      ),
      sortingField: 'name',
      width: 320,
      minWidth: 250
    },
    {
      id: 'active',
      header: 'Status',
      cell: (item: ToolMetadata) => (
        <Box fontSize="body-s">
          {item.isActive ? (
            <span className="tool-status-active">
              Active
            </span>
          ) : (
            <span className="tool-status-inactive">
              Inactive
            </span>
          )}
        </Box>
      ),
      sortingField: 'active',
      width: 100,
      minWidth: 80
    },
    {
      id: 'createdTime',
      header: 'Created',
      cell: (item: ToolMetadata) => (
        <Box fontSize="body-s" color="text-body-secondary">
          {item.createdTime ? new Date(item.createdTime).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'N/A'}
        </Box>
      ),
      sortingField: 'createdTime',
      width: 120,
      minWidth: 100
    }
  ], []);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Manage your tools and their revisions"
        >
          Tools
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} stackItems />
        
        {!canEditTools && (
          <Alert type="info" statusIconAriaLabel="Info">
            You have view-only access to this page. Sufficient permission is required to make changes.
          </Alert>
        )}
        
        <Container
          header={
            <Header
              counter={`(${totalTools})`}
              description="Tools grouped by category"
            >
              Tools by Category
            </Header>
          }
        >
          {isLoading ? (
            <Box textAlign="center" padding="l">
              Loading tools...
            </Box>
          ) : Object.keys(toolsByCategory).length === 0 ? (
            <Box textAlign="center" color="inherit">
              <b>No tools</b>
              <Box
                padding={{ bottom: "s" }}
                variant="p"
                color="inherit"
              >
                No tools to display.
              </Box>
            </Box>
          ) : (
            <SpaceBetween size="m">
              {Object.entries(toolsByCategory)
                .sort(([a], [b]) => a.localeCompare(b)) // Sort categories alphabetically
                .map(([category, categoryTools]) => (
                  <ExpandableSection
                    key={category}
                    headerText={`${category} (${categoryTools.length})`}
                    defaultExpanded={true}
                  >
                    <Table
                      columnDefinitions={columnDefinitions}
                      items={categoryTools}
                      variant="embedded"
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>No tools in this category</b>
                        </Box>
                      }
                    />
                  </ExpandableSection>
                ))}
            </SpaceBetween>
          )}
        </Container>

        {/* Tools Backend Management */}
        {canEditTools && (
        <Container
          header={
            <Header
              variant="h2"
              description="Re-initialize tools backend systems"
            >
              Backend Management
            </Header>
          }
        >
          <SpaceBetween size="m">
            <Box variant="p">
              Use this function to refresh the backend systems for tools management. 
              This will re-initialize the tools backend and may help resolve configuration or connectivity issues.
            </Box>
            <Button
              variant="normal"
              loading={isReloading}
              onClick={handleReloadToolsBackend}
            >
              Reload Tools Backend
            </Button>
          </SpaceBetween>
        </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default ToolsList;
